<template>
  <div>
    <q-table
      v-if="symbol"
      flat
      bordered
      :rows="StockAlphaBetaData"
      binary-state-sort
      separator="cell"
      :columns="columns"
      dense
      hide-bottom
      :class="
        $q.dark.mode
          ? 'table-sticky-header-column-dark'
          : 'table-sticky-header-column'
      "
    />
  </div>
</template>

<script>
import { ref, watch, onMounted } from "vue";
import useData from "src/use/data";
import versionTwo from "src/use/datav2";

export default {
  name: "StockAlphaBeta",
  props: {
    symbol: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { alphaBeta } = useData();
    const { valueAtRisk } = versionTwo();
    const StockAlphaBetaData = ref([]);

    const getData = async (symbol) => {
      try {
        const resValueAtRisk = await valueAtRisk();
        const valueAtRiskStocks = resValueAtRisk.data.filter(
          (i) => i.var_95_cf !== null
        );

        const resAlphaBeta = await alphaBeta(symbol);
        const stockAlphaBetaData = resAlphaBeta.data;

        const mergedData = valueAtRiskStocks
          .map((obj1) => {
            const matchingObj2 = stockAlphaBetaData.find(
              (obj2) => obj1.symbol === obj2.symbol
            );
            if (matchingObj2) {
              return {
                symbol: obj1.symbol,
                var_90_cf: obj1.var_90_cf,
                var_95_cf: obj1.var_95_cf,
                var_99_cf: obj1.var_99_cf,
                std_deviation_monthly: obj1.std_deviation_monthly,
                mean_return_month: obj1.mean_return_month,
                beta_1_months: matchingObj2.beta_1_months,
                alpha_1_months: matchingObj2.alpha_1_months,
                beta_3_months: matchingObj2.beta_3_months,
                alpha_3_months: matchingObj2.alpha_3_months,
                beta_12_months: matchingObj2.beta_12_months,
                alpha_12_months: matchingObj2.alpha_12_months,
              };
            }
            return null;
          })
          .filter(Boolean);

        StockAlphaBetaData.value = props.symbol ? mergedData : [];
      } catch (error) {
        console.error(error);
        return (StockAlphaBetaData.value = []);
      }
    };

    watch(props, (newProps, oldProps) => {
      getData(newProps.symbol);
    });

    const pagination = ref({
      sortBy: "beta_3_months",
      descending: true,
      page: 1,
      rowsPerPage: 10,
    });

    const columns = [
      {
        name: "symbol",
        label: "Symbol",
        field: "symbol",
        align: "left",
        style: "max-width:110px",
        classes: "ellipsis",
      },
      {
        name: "beta_1_months",
        label: "Beta 1M",
        field: (row) => row.beta_1_months,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },

      {
        name: "alpha_1_months",
        label: "Alpha 1M",
        field: (row) => row.alpha_1_months,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },

      {
        name: "beta_3_months",
        label: "Beta 3M",
        field: (row) => row.beta_3_months,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },
      {
        name: "alpha_3_months",
        label: "Alpha 3M",
        field: (row) => row.alpha_3_months,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },

      {
        name: "beta_12_months",
        label: "Beta 12M",
        field: (row) => row.beta_12_months,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },

      {
        name: "alpha_12_months",
        label: "Alpha 12M",
        field: (row) => row.alpha_12_months,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },
      {
        name: "var_95_cf",
        label: "VAR 95 CL",
        field: (row) => row.var_95_cf,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },
      {
        name: "std_deviation_monthly",
        label: "STD M",
        field: (row) => row.std_deviation_monthly,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },
      {
        name: "mean_return_month",
        label: "Mean Return M",
        field: (row) => row.mean_return_month,
        sortable: true,
        format: (val, row) => {
          return parseFloat(val).toFixed(2);
        },
        style: "max-width:30px",
        align: "center",
      },
    ];

    onMounted(() => {
      getData(props.symbol);
    });

    return {
      StockAlphaBetaData,
      columns,
      pagination,
    };
  },
};
</script>
